import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Check,
  X,
  Trash2,
  AlertTriangle,
  Loader,
  AlertCircle,
} from "lucide-react";
import DocumentItem from "./DocumentItem";
import InsertMenu from "./InsertMenu";
import InsertHoverBar from "./InsertHoverBar";
import DocumentSourcesSidebar from "./DocumentSourcesSidebar";
import TemaSearchModal from "./TemaSearchModal";
import PopUp from "./PopUp";
import { useTranslation } from "../hooks/useTranslation";

/**
 * Funções auxiliares para trabalhar com hierarquia de itens
 */

// Verifica se um item é "pai" de outro item (baseado na hierarquia)
const isParentOf = (parentHierarchy, childHierarchy) => {
  if (!parentHierarchy || !childHierarchy) return false;
  // Um item é pai se a hierarquia do filho começa com a hierarquia do pai + "."
  return childHierarchy.startsWith(parentHierarchy + ".");
};

// Encontra todos os filhos de um item pai
const getChildrenItems = (items, parentIndex) => {
  const parentItem = items[parentIndex];
  if (!parentItem || !parentItem.hierarquia) return [];

  const parentHierarchy = parentItem.hierarquia;
  const children = [];

  // Percorrer os itens após o pai
  for (let i = parentIndex + 1; i < items.length; i++) {
    const item = items[i];

    // Se o item não tem hierarquia, continuar procurando (compatibilidade)
    if (!item.hierarquia) {
      continue;
    }

    if (isParentOf(parentHierarchy, item.hierarquia)) {
      // Este item é filho do pai
      children.push({ item, index: i });
    } else {
      // Chegou em um item que não é filho (hierarquia diferente), parar
      break;
    }
  }

  return children;
};

// Verifica se um item tem filhos
const hasChildren = (items, itemIndex) => {
  return getChildrenItems(items, itemIndex).length > 0;
};

// Retorna todos os IDs dos itens que fazem parte da seção (pai + filhos)
const getSectionItemIds = (items, parentIndex) => {
  const parentItem = items[parentIndex];
  if (!parentItem) return [];

  const ids = [parentItem.id];
  const children = getChildrenItems(items, parentIndex);
  children.forEach((child) => ids.push(child.item.id));

  return ids;
};

/** Item pode abrir painel de fontes (flag ou metadados com referência de imagem/página). */
function editorItemHasReferencias(item) {
  if (!item) return false;
  if (item.possui_referencias) return true;
  if (item.type === "image") return true;
  const m = item.metadata || {};
  if (m.token != null && String(m.token).length > 0) {
    if (m.pagina != null || m.coordenadas != null) return true;
  }
  if (m.id_documento != null && m.pagina != null) return true;
  return false;
}

/**
 * Componente principal do editor de documentos jurídicos
 * Genérico para ser usado com qualquer tipo de documento (Contestação, Petição, etc.)
 */
const DocumentEditor = ({
  document,
  onDocumentChange,
  onFinishDocument,
  allDocuments = [],
  originDocuments = [], // Documentos de origem disponíveis para visualização
  documents = [], // Lista de documentos disponíveis (para o select)
  selectedDocumentId = null, // ID do documento selecionado
  onDocumentSelect, // Callback para seleção de documento
  showHeader = true, // Controla se mostra o header interno
  onStatsChange, // Callback para enviar estatísticas para o componente pai
  editavel = true, // Define se o documento é editável
  jobId = null, // ID do job (necessário para buscar referências)
  token = null, // Token de autenticação
  apiBaseUrl = null, // URL base da API
  onOriginDocumentsUpdated = null, // Callback para notificar quando a lista de documentos de origem precisa ser atualizada
  selectItemId = null, // ID do item a ser selecionado externamente (para navegação)
  cellId = null, // ID da célula (necessário para pesquisa de temas)
}) => {
  const { t } = useTranslation("editor");
  const [items, setItems] = useState(document?.items || []);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [showSourceViewer, setShowSourceViewer] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [showInsertMenu, setShowInsertMenu] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [mainPanelWidth, setMainPanelWidth] = useState(50);
  const [showDividers, setShowDividers] = useState(false);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null); // Armazena o ID do item que está fazendo upload

  // Modo "Editar tudo": coloca todos os itens de texto em edição simultaneamente.
  // A toolbar de formatação do Quill fica oculta (CSS) e só reaparece no item focado.
  const [editAllMode, setEditAllMode] = useState(false);

  // Estados para seleção múltipla de revisão
  const [multiSelectMode, setMultiSelectMode] = useState(false); // Modo de seleção múltipla ativado
  const [selectedReviewItems, setSelectedReviewItems] = useState(new Set()); // IDs dos itens selecionados para revisão múltipla

  // Estados para exclusão de seção
  const [sectionDeleteItems, setSectionDeleteItems] = useState([]); // IDs dos itens selecionados para exclusão de seção
  const [showSectionDeleteModal, setShowSectionDeleteModal] = useState(false); // Controla exibição do modal
  const [deletingSectionProgress, setDeletingSectionProgress] = useState(null); // { current: número, total: número }
  const [sectionDeleteError, setSectionDeleteError] = useState(null); // Erro durante exclusão de seção

  // Estados para modal de pesquisa de temas
  const [showTemaSearchModal, setShowTemaSearchModal] = useState(false);
  const [temaInsertPosition, setTemaInsertPosition] = useState(null); // 'above' ou 'below'
  const [temaReferenceItemId, setTemaReferenceItemId] = useState(null); // ID do item de referência
  const [showTemaDisclaimer, setShowTemaDisclaimer] = useState(false); // Disclaimer após importar tema

  const mainResizeRef = useRef(null);

  // Map de instâncias Quill por itemId — usado para mover foco entre itens
  // em fluxos vindos do teclado (Enter cria abaixo, setas navegam, Backspace deleta).
  const quillRefsMap = useRef(new Map());

  const registerQuill = useCallback((itemId, quillInstance) => {
    if (quillInstance) {
      quillRefsMap.current.set(itemId, quillInstance);
    } else {
      quillRefsMap.current.delete(itemId);
    }
  }, []);

  const focusItemAt = useCallback((itemId, position = 'end', delayMs = 60) => {
    // Pequeno delay para esperar o Quill montar caso o item tenha acabado de entrar em edit mode.
    setTimeout(() => {
      const editor = quillRefsMap.current.get(itemId);
      if (!editor) return;
      const length = editor.getLength();
      const index = position === 'end' ? Math.max(0, length - 1) : 0;
      editor.setSelection(index, 0);
      editor.focus();
    }, delayMs);
  }, []);

  // Atualizar o documento pai quando os itens mudarem
  useEffect(() => {
    if (onDocumentChange) {
      onDocumentChange({ ...document, items });
    }

    // Enviar estatísticas para o componente pai
    if (onStatsChange) {
      const totalItems = items.length;
      const reviewedItems = items.filter((item) => item.reviewed).length;
      const reviewProgress =
        totalItems > 0 ? Math.round((reviewedItems / totalItems) * 100) : 0;
      onStatsChange({ totalItems, reviewedItems, reviewProgress });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Limpar fontes quando o item selecionado mudar (sem buscar automaticamente)
  useEffect(() => {
    if (!selectedItemId) {
      // Nenhum item selecionado, limpar as fontes
      setSelectedSources({
        origem_dados: [],
        origem_escrita: [],
        origem_usuario: [],
      });
    }
  }, [selectedItemId]);

  // Selecionar item externamente quando selectItemId mudar
  useEffect(() => {
    if (selectItemId && items.some((item) => item.id === selectItemId)) {
      // Se estiver no modo de seleção múltipla, selecionar para revisão em vez de expandir
      if (multiSelectMode) {
        handleToggleReview(selectItemId, true);
      } else {
        setSelectedItemId(selectItemId);
      }

      // Fazer scroll até o item após um pequeno delay
      setTimeout(() => {
        const element = window.document.getElementById(
          `document-item-${selectItemId}`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          // Destacar o item brevemente
          element.style.transition = "box-shadow 0.3s ease";
          element.style.boxShadow = "0 0 0 4px rgba(255, 214, 10, 0.5)";
          setTimeout(() => {
            element.style.boxShadow = "";
          }, 2000);
        }
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectItemId, items, multiSelectMode]);

  // Gerenciar redimensionamento do painel principal
  useEffect(() => {
    const handleMainResize = (e) => {
      if (mainResizeRef.current) {
        const newWidth = (e.clientX / window.innerWidth) * 100;
        if (newWidth > 30 && newWidth < 85) {
          setMainPanelWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      mainResizeRef.current = false;
    };

    window.document.addEventListener("mousemove", handleMainResize);
    window.document.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.document.removeEventListener("mousemove", handleMainResize);
      window.document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleItemClick = (itemId) => {
    // Evitar recarregar se o mesmo item já está selecionado
    const isSameItem = selectedItemId === itemId;

    setSelectedItemId(itemId);
    setShowInsertMenu(null);

    // Se o painel de referências está aberto E é um item diferente, buscar referências automaticamente
    if (showSourceViewer && !isSameItem) {
      const item = items.find((i) => i.id === itemId);
      if (item && editorItemHasReferencias(item)) {
        handleViewSources(item);
      } else {
        // Limpar referências se o item não possui referências
        setSelectedSources({
          origem_dados: [],
          origem_escrita: [],
          origem_usuario: [],
        });
      }
    }
  };

  const handleEditClick = (itemId) => {
    setEditingItemId(itemId);
    setSelectedItemId(itemId);
    setSaveError(null); // Limpar erro ao iniciar nova edição
  };

  const handleContentChange = (itemId, newContent) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, content: newContent } : item,
      ),
    );
  };

  const handleSaveEdit = async (itemId, newContent) => {
    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error("Informações necessárias para salvar não disponíveis");
      setSaveError("Erro: informações necessárias não disponíveis");
      return;
    }

    // Verificar se o conteúdo foi fornecido
    if (newContent === undefined || newContent === null) {
      console.error("Conteúdo não fornecido");
      setSaveError("Erro: conteúdo não fornecido");
      return;
    }

    // Iniciar salvamento
    setSavingEdit(true);
    setSaveError(null);

    // DEBUG: Log do item que está sendo editado
    const itemToEdit = items.find((i) => i.id === itemId);
    console.log("🔍 Item sendo editado:", {
      id: itemId,
      type: itemToEdit?.type,
      tipo_item: itemToEdit?.tipo_item,
      metadata: itemToEdit?.metadata,
    });

    try {
      const url = `${apiBaseUrl}/doc_creator/review/edit-item/${jobId}`;
      console.log("💾 Salvando edição do item:", url);
      console.log("📝 Payload:", {
        documento_id: document.id,
        item_id: itemId,
        novo_conteudo: newContent,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          documento_id: document.id,
          item_id: itemId,
          novo_conteudo: newContent,
        }),
      });

      if (!response.ok) {
        // Tratar erros específicos
        let errorMsg = "Erro ao salvar alterações";

        if (response.status === 403) {
          errorMsg = "Você não tem permissão para editar este item.";
        } else if (response.status === 404) {
          errorMsg = "Item não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log("✅ Item salvo com sucesso:", data);

      // Atualizar o item com o novo conteúdo e marcar como revisado
      setItems((current) =>
        current.map((i) =>
          i.id === itemId ? { ...i, content: newContent, reviewed: true } : i,
        ),
      );

      // Sucesso: fechar o editor SE ainda estamos editando este item.
      // Em fluxos de auto-save acionados por blur (ex: ao navegar para o próximo item),
      // o editor pode ter movido para outro item — não devemos sobrescrever isso.
      setEditingItemId((current) => (current === itemId ? null : current));
      setSaveError(null);
    } catch (error) {
      console.error("❌ Erro ao salvar item:", error);
      setSaveError(error.message || "Erro desconhecido ao salvar");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = (itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (item && item.type === "text" && !item.content) {
      setItems(items.filter((i) => i.id !== itemId));
    }
    setEditingItemId(null);
    setSaveError(null); // Limpar erro ao cancelar edição
  };

  // ===== Fluxo fluido de edição (Abordagem A) =====
  // Enter em linha vazia: cria item de texto vazio abaixo do atual e entra em edição.
  // Reusa o pipeline existente de criação (handleInsertItem), que já chama o endpoint,
  // adiciona ao state e ativa editingItemId. Aqui só precisamos focar o Quill novo.
  const handleCreateBelow = useCallback(async (itemId) => {
    const newItem = await handleInsertItem(itemId, "below", "text");
    if (newItem && newItem.type === "text") {
      focusItemAt(newItem.id, "start", 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItemAt]);

  // Backspace em item completamente vazio: deleta o item e foca o anterior no fim.
  const handleDeleteAndFocusPrev = useCallback(async (itemId) => {
    const currentIndex = items.findIndex((i) => i.id === itemId);
    if (currentIndex <= 0) return; // não há item anterior
    const prevItem = items[currentIndex - 1];
    await handleDeleteItem(itemId);
    if (prevItem && prevItem.type === "text") {
      setEditingItemId(prevItem.id);
      setSelectedItemId(prevItem.id);
      focusItemAt(prevItem.id, "end", 80);
    } else if (prevItem) {
      setSelectedItemId(prevItem.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, focusItemAt]);

  // ArrowUp na primeira linha → entra em edição do item anterior (texto) no fim.
  const handleNavigatePrev = useCallback((itemId) => {
    const currentIndex = items.findIndex((i) => i.id === itemId);
    if (currentIndex <= 0) return;
    const prevItem = items[currentIndex - 1];
    if (prevItem.type === "text") {
      setEditingItemId(prevItem.id);
      setSelectedItemId(prevItem.id);
      focusItemAt(prevItem.id, "end", 60);
    } else {
      setSelectedItemId(prevItem.id);
    }
  }, [items, focusItemAt]);

  // ArrowDown na última linha → entra em edição do próximo item (texto) no início.
  const handleNavigateNext = useCallback((itemId) => {
    const currentIndex = items.findIndex((i) => i.id === itemId);
    if (currentIndex < 0 || currentIndex >= items.length - 1) return;
    const nextItem = items[currentIndex + 1];
    if (nextItem.type === "text") {
      setEditingItemId(nextItem.id);
      setSelectedItemId(nextItem.id);
      focusItemAt(nextItem.id, "start", 60);
    } else {
      setSelectedItemId(nextItem.id);
    }
  }, [items, focusItemAt]);

  const confirmDelete = (itemId) => {
    setShowDeleteConfirm(itemId);
  };

  const handleDeleteItem = async (itemId) => {
    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error(
        "Informações necessárias para excluir item não disponíveis",
      );
      alert("Erro: informações necessárias não disponíveis");
      setShowDeleteConfirm(null);
      return;
    }

    // Guardar o estado anterior para poder reverter em caso de erro
    const previousItems = [...items];

    // Atualizar estado local otimisticamente (remover o item imediatamente)
    setItems(items.filter((item) => item.id !== itemId));
    setSelectedItemId(null);
    setShowDeleteConfirm(null);

    try {
      const url = `${apiBaseUrl}/doc_creator/review/delete-item/${jobId}`;
      console.log("🗑️ Excluindo item:", url);
      console.log("📝 Dados enviados:", {
        documento_id: document.id,
        item_id: itemId,
      });

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          documento_id: document.id,
          item_id: itemId,
        }),
      });

      if (!response.ok) {
        // Reverter o estado local em caso de erro
        setItems(previousItems);

        let errorMsg = "Erro ao excluir item";

        if (response.status === 403) {
          errorMsg = "Você não tem permissão para excluir este item.";
        } else if (response.status === 404) {
          errorMsg = "Item não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        console.error("❌ Erro ao excluir item:", errorMsg);
        alert(errorMsg);
        return;
      }

      const data = await response.json();
      console.log("✅ Item excluído com sucesso:", data);
      console.log(`📊 Total de itens restantes: ${data.total_itens_restantes}`);
    } catch (error) {
      // Reverter o estado local em caso de erro de rede
      console.error("❌ Erro ao excluir item:", error);
      setItems(previousItems);
      alert("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  const handleDuplicateItem = async (itemId) => {
    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error("Informações necessárias para clonar item não disponíveis");
      alert("Erro: informações necessárias não disponíveis");
      return;
    }

    // Encontrar o item que será clonado
    const itemIndex = items.findIndex((i) => i.id === itemId);
    if (itemIndex === -1) {
      console.error("Item não encontrado");
      return;
    }

    try {
      const url = `${apiBaseUrl}/doc_creator/review/clone-item/${jobId}`;
      console.log("📋 Clonando item:", url);
      console.log("📝 Dados enviados:", {
        documento_id: document.id,
        item_id: itemId,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          documento_id: document.id,
          item_id: itemId,
        }),
      });

      if (!response.ok) {
        let errorMsg = "Erro ao clonar item";

        if (response.status === 403) {
          errorMsg = "Você não tem permissão para clonar este item.";
        } else if (response.status === 404) {
          errorMsg = "Item não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        console.error("❌ Erro ao clonar item:", errorMsg);
        alert(errorMsg);
        return;
      }

      const data = await response.json();
      console.log("✅ Item clonado com sucesso:", data);
      console.log(
        "📦 Item clonado retornado pela API:",
        JSON.stringify(data.item_clonado, null, 2),
      );

      // Processar o item clonado retornado pela API
      const apiClonedItem = data.item_clonado;
      const itemToDuplicate = items[itemIndex];
      const tipoItem = apiClonedItem.tipo_item;

      let content = "";
      let imageUrl = null;
      let caption = null;

      if (tipoItem === "TEXTO") {
        content = apiClonedItem.metadados?.texto || "";
      } else if (tipoItem === "IMAGEM") {
        imageUrl = itemToDuplicate.imageUrl; // Manter a URL da imagem original se existir
        caption = apiClonedItem.metadados?.texto_de_interesse || "";
      }

      const clonedItem = {
        id: apiClonedItem.id,
        type: itemToDuplicate.type,
        content: content,
        imageUrl: imageUrl,
        caption: caption,
        reviewed:
          apiClonedItem.aprovado_por !== null &&
          apiClonedItem.data_aprovacao !== null,
        possui_referencias:
          (apiClonedItem.referencias_dados &&
            apiClonedItem.referencias_dados.length > 0) ||
          (apiClonedItem.referencias_escrita &&
            apiClonedItem.referencias_escrita.length > 0) ||
          (apiClonedItem.referencias_usuario &&
            apiClonedItem.referencias_usuario.length > 0) ||
          tipoItem === "IMAGEM",
        metadata: apiClonedItem.metadados || {},
        aprovado_por: apiClonedItem.aprovado_por,
        data_aprovacao: apiClonedItem.data_aprovacao,
        tipo_item: tipoItem,
        // Indicador de item criado por IA
        indicador_ia: apiClonedItem.indicador_ia,
      };

      console.log(
        "🔧 Item clonado convertido para o editor:",
        JSON.stringify(clonedItem, null, 2),
      );

      // Inserir o clone logo abaixo do original
      const newItems = [...items];
      newItems.splice(itemIndex + 1, 0, clonedItem);
      setItems(newItems);
    } catch (error) {
      console.error("❌ Erro ao clonar item:", error);
      alert("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  const handleMoveItemUp = async (itemId) => {
    const itemIndex = items.findIndex((i) => i.id === itemId);

    // Verificar se pode mover para cima
    if (itemIndex === 0) {
      console.warn("Item já está na primeira posição");
      return;
    }

    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error("Informações necessárias para mover item não disponíveis");
      alert("Erro: informações necessárias não disponíveis");
      return;
    }

    // Atualizar estado local otimisticamente (antes da resposta da API)
    const newItems = [...items];
    [newItems[itemIndex - 1], newItems[itemIndex]] = [
      newItems[itemIndex],
      newItems[itemIndex - 1],
    ];
    setItems(newItems);

    try {
      const url = `${apiBaseUrl}/doc_creator/review/move-item-up/${jobId}`;
      console.log("⬆️ Movendo item para cima:", url);
      console.log("📝 Dados enviados:", {
        documento_id: document.id,
        item_id: itemId,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          documento_id: document.id,
          item_id: itemId,
        }),
      });

      if (!response.ok) {
        // Reverter o estado local em caso de erro
        setItems(items);

        let errorMsg = "Erro ao mover item para cima";

        if (response.status === 400) {
          errorMsg = "Item já está na primeira posição.";
        } else if (response.status === 403) {
          errorMsg = "Você não tem permissão para mover este item.";
        } else if (response.status === 404) {
          errorMsg = "Item não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        console.error("❌ Erro ao mover item para cima:", errorMsg);
        alert(errorMsg);
        return;
      }

      const data = await response.json();
      console.log("✅ Item movido para cima com sucesso:", data);
    } catch (error) {
      // Reverter o estado local em caso de erro de rede
      console.error("❌ Erro ao mover item para cima:", error);
      setItems(items);
      alert("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  const handleMoveItemDown = async (itemId) => {
    const itemIndex = items.findIndex((i) => i.id === itemId);

    // Verificar se pode mover para baixo
    if (itemIndex === items.length - 1) {
      console.warn("Item já está na última posição");
      return;
    }

    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error("Informações necessárias para mover item não disponíveis");
      alert("Erro: informações necessárias não disponíveis");
      return;
    }

    // Atualizar estado local otimisticamente (antes da resposta da API)
    const newItems = [...items];
    [newItems[itemIndex], newItems[itemIndex + 1]] = [
      newItems[itemIndex + 1],
      newItems[itemIndex],
    ];
    setItems(newItems);

    try {
      const url = `${apiBaseUrl}/doc_creator/review/move-item-down/${jobId}`;
      console.log("⬇️ Movendo item para baixo:", url);
      console.log("📝 Dados enviados:", {
        documento_id: document.id,
        item_id: itemId,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          documento_id: document.id,
          item_id: itemId,
        }),
      });

      if (!response.ok) {
        // Reverter o estado local em caso de erro
        setItems(items);

        let errorMsg = "Erro ao mover item para baixo";

        if (response.status === 400) {
          errorMsg = "Item já está na última posição.";
        } else if (response.status === 403) {
          errorMsg = "Você não tem permissão para mover este item.";
        } else if (response.status === 404) {
          errorMsg = "Item não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        console.error("❌ Erro ao mover item para baixo:", errorMsg);
        alert(errorMsg);
        return;
      }

      const data = await response.json();
      console.log("✅ Item movido para baixo com sucesso:", data);
    } catch (error) {
      // Reverter o estado local em caso de erro de rede
      console.error("❌ Erro ao mover item para baixo:", error);
      setItems(items);
      alert("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  const handleInsertItem = async (itemId, position, type) => {
    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error("Informações necessárias para criar item não disponíveis");
      alert("Erro: informações necessárias não disponíveis");
      return;
    }

    // Encontrar o índice do item de referência
    const itemIndex = items.findIndex((i) => i.id === itemId);
    if (itemIndex === -1) {
      console.error("Item de referência não encontrado");
      return;
    }

    // Determinar item_acima_id e item_abaixo_id baseado na posição
    let item_acima_id = null;
    let item_abaixo_id = null;

    if (position === "above") {
      // Inserir ACIMA do itemId
      item_acima_id = itemIndex > 0 ? items[itemIndex - 1].id : null;
      item_abaixo_id = itemId;
    } else {
      // Inserir ABAIXO do itemId
      item_acima_id = itemId;
      item_abaixo_id =
        itemIndex < items.length - 1 ? items[itemIndex + 1].id : null;
    }

    // Converter tipo para o formato da API
    const tipoApi = type === "text" ? "TEXTO" : "IMAGEM";

    // Preparar body baseado no tipo
    let requestBody = {
      documento_id: document.id,
      tipo: tipoApi,
      item_acima_id: item_acima_id,
      item_abaixo_id: item_abaixo_id,
    };

    if (tipoApi === "TEXTO") {
      // Para TEXTO: campo 'conteudo' é obrigatório
      requestBody.conteudo = ""; // Será preenchido pelo usuário após criação
    } else if (tipoApi === "IMAGEM") {
      // Para IMAGEM: campos 'id_item' e 'texto_de_interesse' são obrigatórios
      // Como estamos criando um placeholder vazio, usar valores temporários
      requestBody.id_item = "temp"; // Será atualizado depois
      requestBody.texto_de_interesse = ""; // Será preenchido pelo usuário após criação
    }

    try {
      const url = `${apiBaseUrl}/doc_creator/review/create-item/${jobId}`;
      console.log("➕ Criando novo item:", url);
      console.log("📝 Dados enviados:", requestBody);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMsg = "Erro ao criar item";

        if (response.status === 400) {
          try {
            const errorData = await response.json();
            errorMsg = errorData?.detail || "Erro de validação ao criar item.";
          } catch {
            errorMsg = "Erro de validação ao criar item.";
          }
        } else if (response.status === 403) {
          errorMsg = "Você não tem permissão para criar itens.";
        } else if (response.status === 404) {
          errorMsg = "Documento não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        console.error("❌ Erro ao criar item:", errorMsg);
        alert(errorMsg);
        return;
      }

      const data = await response.json();
      console.log("✅ Item criado com sucesso:", data);
      console.log(
        "📦 Item completo retornado pela API:",
        JSON.stringify(data.item_criado, null, 2),
      );
      console.log(
        `📊 Posição: ${data.posicao}, Total de itens: ${data.total_itens}`,
      );

      // Converter o item retornado pela API para o formato do editor
      // A nova estrutura da API retorna:
      // - tipo_item: "TEXTO" ou "IMAGEM"
      // - metadados: { texto: "..." } para TEXTO ou { id_item, texto_de_interesse, ... } para IMAGEM
      // - referencias_dados: []
      // - referencias_escrita: []
      // - aprovado_por: null
      // - data_aprovacao: null

      const apiItem = data.item_criado;
      const tipoItem = apiItem.tipo_item; // Agora sempre vem como 'tipo_item'

      let content = "";
      let imageUrl = null;
      let caption = null;

      if (tipoItem === "TEXTO") {
        // Para TEXTO: conteúdo vem em metadados.texto
        content = apiItem.metadados?.texto || "";
      } else if (tipoItem === "IMAGEM") {
        // Para IMAGEM: não há URL ainda (será carregada com token)
        imageUrl = null;
        // Caption vem do texto_de_interesse
        caption = apiItem.metadados?.texto_de_interesse || "";
      }

      const newItem = {
        id: apiItem.id,
        type: type, // 'text' ou 'image' (formato do frontend)
        content: content,
        imageUrl: imageUrl,
        caption: caption,
        reviewed:
          apiItem.aprovado_por !== null && apiItem.data_aprovacao !== null,
        possui_referencias:
          (apiItem.referencias_dados && apiItem.referencias_dados.length > 0) ||
          (apiItem.referencias_escrita &&
            apiItem.referencias_escrita.length > 0) ||
          (apiItem.referencias_usuario &&
            apiItem.referencias_usuario.length > 0) ||
          tipoItem === "IMAGEM",
        // Armazenar metadados originais da API
        metadata: apiItem.metadados || {},
        aprovado_por: apiItem.aprovado_por,
        data_aprovacao: apiItem.data_aprovacao,
        // IMPORTANTE: Preservar tipo_item para que o endpoint de edição possa validar
        tipo_item: tipoItem,
        // Indicador de item criado por IA
        indicador_ia: apiItem.indicador_ia,
      };

      console.log(
        "🔧 Item convertido para o editor:",
        JSON.stringify(newItem, null, 2),
      );

      // Inserir o novo item na posição correta
      const newItems = [...items];
      const insertIndex = position === "above" ? itemIndex : itemIndex + 1;
      newItems.splice(insertIndex, 0, newItem);
      setItems(newItems);

      // Abrir o item para edição
      setEditingItemId(newItem.id);
      setSelectedItemId(newItem.id);
      setShowInsertMenu(null);

      // Scroll para o novo item após um pequeno delay
      setTimeout(() => {
        const element = window.document.getElementById(
          `document-item-${newItem.id}`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);

      return newItem;
    } catch (error) {
      console.error("❌ Erro ao criar item:", error);
      alert("Erro de conexão. Verifique sua internet e tente novamente.");
    }
    return undefined;
  };

  const handleToggleReview = async (itemId, isMultiSelectClick = false) => {
    // Se estiver no modo de seleção múltipla, apenas adicionar/remover da seleção
    if (multiSelectMode || isMultiSelectClick) {
      // Verificar se o item já foi revisado - não permitir selecionar
      const item = items.find((i) => i.id === itemId);
      if (item && item.reviewed) {
        // Item já foi revisado, não permitir selecionar
        return;
      }

      setSelectedReviewItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
      return; // Não processar ainda, apenas adicionar à seleção
    }

    // Se não estiver no modo de seleção múltipla, processar normalmente
    await processReviewToggle([itemId]);
  };

  // Processa a revisão de todos os itens selecionados
  const handleApproveSelectedItems = async () => {
    if (selectedReviewItems.size === 0) return;

    const itemsToProcess = Array.from(selectedReviewItems);
    setSelectedReviewItems(new Set()); // Limpar seleção
    await processReviewToggle(itemsToProcess);
  };

  // Processa a revisão de um ou múltiplos itens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processReviewToggle = useCallback(async (itemIds) => {
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error(
        "Informações necessárias para alternar revisão não disponíveis",
      );
      return;
    }

    if (itemIds.length === 0) return;

    // Buscar os itens atuais para saber o estado
    const itemsToToggle = items.filter((i) => itemIds.includes(i.id));
    if (itemsToToggle.length === 0) {
      console.error("Itens não encontrados");
      return;
    }

    // Determinar o estado desejado: se todos estão revisados, desmarcar todos; caso contrário, marcar todos como revisados
    const allReviewed = itemsToToggle.every((item) => item.reviewed);
    const newReviewedState = !allReviewed;

    // Atualizar estado local otimisticamente (antes da resposta da API)
    setItems(
      items.map((i) =>
        itemIds.includes(i.id) ? { ...i, reviewed: newReviewedState } : i,
      ),
    );

    // Processar cada item individualmente
    const promises = itemIds.map(async (itemId) => {
      const item = items.find((i) => i.id === itemId);
      if (!item) return { success: false, itemId };

      try {
        const url = `${apiBaseUrl}/doc_creator/review/toggle-item-review/${jobId}`;
        const precisa_revisao = !newReviewedState;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
          body: JSON.stringify({
            documento_id: document.id,
            item_id: itemId,
            precisa_revisao: precisa_revisao,
          }),
        });

        if (!response.ok) {
          let errorMsg = "Erro ao alterar estado de revisão";

          if (response.status === 403) {
            errorMsg = "Você não tem permissão para marcar este item.";
          } else if (response.status === 404) {
            errorMsg = "Item não encontrado.";
          } else if (response.status === 500) {
            errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
          } else {
            try {
              const errorData = await response.json();
              errorMsg =
                errorData?.message ||
                errorData?.detail ||
                `Erro ${response.status}: ${response.statusText}`;
            } catch {
              errorMsg = `Erro ${response.status}: ${response.statusText}`;
            }
          }

          return { success: false, itemId, error: errorMsg };
        }

        const data = await response.json();
        return { success: true, itemId, data };
      } catch (error) {
        console.error(`❌ Erro ao alternar revisão do item ${itemId}:`, error);
        return { success: false, itemId, error: "Erro de conexão" };
      }
    });

    const results = await Promise.all(promises);

    // Verificar se houve erros
    const errors = results.filter((r) => !r.success);
    if (errors.length > 0) {
      // Reverter o estado local em caso de erro
      setItems(
        items.map((i) => {
          if (itemIds.includes(i.id)) {
            const originalItem = itemsToToggle.find((it) => it.id === i.id);
            return originalItem ? { ...i, reviewed: originalItem.reviewed } : i;
          }
          return i;
        }),
      );

      const errorMsg =
        errors.length === 1
          ? errors[0].error
          : `Erro ao processar ${errors.length} de ${itemIds.length} itens`;
      alert(errorMsg);
      return;
    }

    console.log(`✅ ${results.length} item(ns) processado(s) com sucesso`);
    // Limpar seleção múltipla após processar
    setSelectedReviewItems(new Set());
  });

  // Processa a revisão dos itens selecionados quando o usuário soltar Ctrl
  useEffect(() => {
    const handleKeyUp = (e) => {
      // Se Ctrl foi solto e há itens selecionados, processar automaticamente
      if (
        (e.key === "Control" || e.key === "Meta") &&
        selectedReviewItems.size > 0
      ) {
        // Aguardar um pouco para garantir que o último clique foi processado
        setTimeout(() => {
          if (selectedReviewItems.size > 0) {
            const itemsToProcess = Array.from(selectedReviewItems);
            setSelectedReviewItems(new Set()); // Limpar seleção
            processReviewToggle(itemsToProcess);
          }
        }, 200);
      }
    };

    window.addEventListener("keyup", handleKeyUp);
    return () => window.removeEventListener("keyup", handleKeyUp);
  }, [processReviewToggle, selectedReviewItems]);

  const handleViewSources = async (item) => {
    // Fazer chamada à API para buscar referências do item
    if (!jobId || !document?.id || !item?.id) {
      console.warn(
        "Informações necessárias não disponíveis para buscar referências",
      );
      return;
    }

    // Atualizar o item selecionado sem acionar novo fetch
    setSelectedItemId(item.id);
    setLoadingReferences(true);
    setShowSourceViewer(true); // Abrir o sidebar imediatamente

    try {
      const url = `${apiBaseUrl}/doc_creator/review/item-references/${jobId}/${document.id}/${item.id}`;
      console.log("🔍 Buscando referências do item:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erro ao buscar referências: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      console.log("✅ Referências do item recebidas:", data);

      // Processar as referências para o formato esperado pela sidebar
      const processedSources = {
        origem_dados: data.origem_dados || [],
        origem_escrita: data.origem_escrita || [],
        origem_usuario: data.origem_usuario || [],
      };

      setSelectedSources(processedSources);
    } catch (error) {
      console.error("❌ Erro ao buscar referências:", error);
      // Limpar fontes em caso de erro
      setSelectedSources({
        origem_dados: [],
        origem_escrita: [],
        origem_usuario: [],
      });
    } finally {
      setLoadingReferences(false);
    }
  };

  const handleImageUpload = async (itemId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error(
        "Informações necessárias para upload de imagem não disponíveis",
      );
      alert("Erro: informações necessárias não disponíveis");
      return;
    }

    // Validar tipo de arquivo (imagens comuns)
    const validImageTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/bmp",
      "image/tiff",
      "image/webp",
    ];
    if (!validImageTypes.includes(file.type)) {
      alert(
        "Por favor, selecione um arquivo de imagem válido (PNG, JPG, JPEG, GIF, BMP, TIFF, WEBP).",
      );
      return;
    }

    // Validar tamanho do arquivo (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert("O arquivo é muito grande. O tamanho máximo é 50MB.");
      return;
    }

    try {
      // Iniciar loading
      setUploadingImage(itemId);

      console.log("📤 Iniciando upload de imagem:", {
        nome: file.name,
        tamanho: file.size,
        tipo: file.type,
        jobId,
        documentoId: document.id,
        itemId,
      });

      // Criar FormData
      const formData = new FormData();
      formData.append("arquivo_imagem", file);
      formData.append("documento_id", document.id);
      formData.append("item_id", itemId);

      // Fazer upload
      const url = `${apiBaseUrl}/doc_creator/review/add-image-as-origin/${jobId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMsg = "Erro ao fazer upload da imagem";

        if (response.status === 403) {
          errorMsg = "Você não tem permissão para fazer upload de imagem.";
        } else if (response.status === 404) {
          errorMsg = "Job não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          try {
            const errorData = await response.json();
            errorMsg =
              errorData?.message ||
              errorData?.detail ||
              `Erro ${response.status}: ${response.statusText}`;
          } catch {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        throw new Error(errorMsg);
      }

      const result = await response.json();
      console.log("✅ Upload de imagem concluído com sucesso:", result);

      // Atualizar o item com as informações retornadas pela API
      // O retorno contém: referencia_imagem com { id_item, id_documento, pagina, coordenadas, token }
      setItems(
        items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              // Atualizar metadata com as informações da referência
              metadata: {
                ...item.metadata,
                token: result.referencia_imagem.token,
                coordenadas: result.referencia_imagem.coordenadas,
                pagina: result.referencia_imagem.pagina,
                documentoOrigemId: result.referencia_imagem.id_documento,
                arquivoOriginal: result.arquivo_original,
                arquivoPdfGerado: result.arquivo_pdf_gerado,
                numPaginas: result.num_paginas,
              },
              // Marcar como tendo referências
              possui_referencias: true,
            };
          }
          return item;
        }),
      );

      console.log("✅ Item atualizado com referência da imagem");

      // Notificar componente pai para recarregar a lista de documentos de origem
      // (pois uma nova imagem foi adicionada como documento de origem)
      if (onOriginDocumentsUpdated) {
        console.log("🔄 Notificando para recarregar documentos de origem...");
        await onOriginDocumentsUpdated();
      }

      // IMPORTANTE: Recarregar as referências do item para mostrar a nova referência
      // Fazer isso apenas se o painel de referências está aberto E o item está selecionado
      if (showSourceViewer && selectedItemId === itemId) {
        console.log(
          "🔄 Recarregando referências do item após upload de imagem...",
        );
        const item = items.find((i) => i.id === itemId);
        if (item) {
          // Aguardar um pequeno delay para garantir que o backend processou tudo
          await new Promise((resolve) => setTimeout(resolve, 500));
          await handleViewSources(item);
        }
      }
    } catch (error) {
      console.error("❌ Erro ao fazer upload da imagem:", error);
      alert(
        error.message ||
          "Erro ao fazer upload da imagem. Verifique sua conexão e tente novamente.",
      );
    } finally {
      // Limpar loading
      setUploadingImage(null);
    }
  };

  // Handler para abrir o modal de pesquisa de temas
  const handleOpenTemaSearch = useCallback((itemId, position) => {
    setTemaInsertPosition(position);
    setTemaReferenceItemId(itemId);
    setShowTemaSearchModal(true);
    setShowInsertMenu(null);
  }, []);

  // Handler para fechar o modal de pesquisa de temas
  const handleCloseTemaSearch = useCallback(() => {
    setShowTemaSearchModal(false);
    setTemaInsertPosition(null);
    setTemaReferenceItemId(null);
  }, []);

  // Handler para importar um tema do modal de pesquisa
  const handleImportTema = useCallback(
    async (temaData) => {
      // Verificar se temos todas as informações necessárias
      if (!jobId || !document?.id || !apiBaseUrl || !token) {
        console.error(
          "Informações necessárias para criar item de tema não disponíveis",
        );
        throw new Error("Informações necessárias não disponíveis");
      }

      const { id_chunk_tema, position, referenceItemId } = temaData;

      // Encontrar o índice do item de referência
      const itemIndex = items.findIndex((i) => i.id === referenceItemId);
      if (itemIndex === -1) {
        console.error("Item de referência não encontrado");
        throw new Error("Item de referência não encontrado");
      }

      // Determinar item_acima_id e item_abaixo_id baseado na posição
      let item_acima_id = null;
      let item_abaixo_id = null;

      if (position === "above") {
        item_acima_id = itemIndex > 0 ? items[itemIndex - 1].id : null;
        item_abaixo_id = referenceItemId;
      } else {
        item_acima_id = referenceItemId;
        item_abaixo_id =
          itemIndex < items.length - 1 ? items[itemIndex + 1].id : null;
      }

      // Preparar body para criação de item a partir do tema
      const requestBody = {
        documento_id: document.id,
        id_chunk_tema: id_chunk_tema,
        item_acima_id: item_acima_id,
        item_abaixo_id: item_abaixo_id,
      };

      try {
        const url = `${apiBaseUrl}/doc_creator/review/create-item-from-tema/${jobId}`;
        console.log("➕ Criando item a partir do tema:", url);
        console.log("📝 Dados enviados:", requestBody);

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          let errorMsg = "Erro ao criar item a partir do tema";

          // Tentar obter mensagem de erro da API primeiro
          try {
            const errorData = await response.json();
            errorMsg = errorData?.detail || errorData?.message || errorMsg;
          } catch {
            // Se não conseguir parsear JSON, usar mensagens padrão por código HTTP
            if (response.status === 400) {
              errorMsg = "Parâmetros inválidos ou posicionamento inválido.";
            } else if (response.status === 403) {
              errorMsg =
                "Sem permissão para acessar o job ou a célula do tema.";
            } else if (response.status === 404) {
              errorMsg = "Tema, documento ou job não encontrado.";
            } else if (response.status === 500) {
              errorMsg =
                "Erro interno do servidor. Tente novamente mais tarde.";
            }
          }

          throw new Error(errorMsg);
        }

        const data = await response.json();
        console.log("✅ Itens criados a partir do tema com sucesso:", data);

        const apiItems = data.itens_criados;

        if (!apiItems || !Array.isArray(apiItems) || apiItems.length === 0) {
          throw new Error("A API não retornou itens criados.");
        }

        console.log(`📦 Total de itens retornados: ${apiItems.length}`);

        // Converter cada item da API para o formato do editor
        const newEditorItems = apiItems.map((apiItem) => {
          const tipoItem = apiItem.tipo_item;

          const possuiReferencias =
            (apiItem.referencias_dados &&
              apiItem.referencias_dados.length > 0) ||
            (apiItem.referencias_escrita &&
              apiItem.referencias_escrita.length > 0) ||
            (apiItem.referencias_usuario &&
              apiItem.referencias_usuario.length > 0) ||
            tipoItem === "IMAGEM";

          if (tipoItem === "IMAGEM") {
            return {
              id: apiItem.id,
              type: "image",
              content: "",
              imageUrl: null,
              caption: apiItem.metadados?.texto_de_interesse || "",
              reviewed:
                apiItem.aprovado_por !== null &&
                apiItem.data_aprovacao !== null,
              possui_referencias: possuiReferencias,
              metadata: apiItem.metadados,
              aprovado_por: apiItem.aprovado_por,
              data_aprovacao: apiItem.data_aprovacao,
              tipo_item: tipoItem,
              indicador_ia: apiItem.indicador_ia,
            };
          } else {
            // TEXTO (default)
            return {
              id: apiItem.id,
              type: "text",
              content: apiItem.metadados?.texto || "",
              reviewed:
                apiItem.aprovado_por !== null &&
                apiItem.data_aprovacao !== null,
              possui_referencias: possuiReferencias,
              metadata: apiItem.metadados,
              aprovado_por: apiItem.aprovado_por,
              data_aprovacao: apiItem.data_aprovacao,
              tipo_item: apiItem.tipo_item || "TEXTO",
              indicador_ia: apiItem.indicador_ia,
            };
          }
        });

        console.log(
          "🔧 Itens de tema convertidos para o editor:",
          JSON.stringify(newEditorItems, null, 2),
        );

        // Inserir todos os itens na posição correta
        const newItems = [...items];
        const insertIndex = position === "above" ? itemIndex : itemIndex + 1;
        newItems.splice(insertIndex, 0, ...newEditorItems);
        setItems(newItems);

        // Selecionar o primeiro novo item
        if (newEditorItems.length > 0) {
          setSelectedItemId(newEditorItems[0].id);
        }

        // Scroll para o primeiro novo item após um pequeno delay
        setTimeout(() => {
          if (newEditorItems.length > 0) {
            const element = window.document.getElementById(
              `document-item-${newEditorItems[0].id}`,
            );
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        }, 100);

        // Mostrar disclaimer após importação bem-sucedida
        setShowTemaDisclaimer(true);

        console.log(
          `✅ Tema importado com sucesso! ${newEditorItems.length} item(ns) adicionado(s).`,
        );

        // Recarregar documentos de origem para incluir possíveis novos processos trazidos pelo tema
        if (onOriginDocumentsUpdated) {
          console.log(
            "🔄 Recarregando documentos de origem após importação de tema...",
          );
          await onOriginDocumentsUpdated();
        }
      } catch (error) {
        console.error("❌ Erro ao importar tema:", error);
        throw error;
      }
    },
    [jobId, document?.id, apiBaseUrl, token, items, onOriginDocumentsUpdated],
  );

  // Handler para iniciar exclusão de seção
  const handleDeleteSectionStart = useCallback(
    (itemId) => {
      const itemIndex = items.findIndex((i) => i.id === itemId);
      if (itemIndex === -1) return;

      // Obter todos os IDs da seção (pai + filhos)
      const sectionIds = getSectionItemIds(items, itemIndex);

      if (sectionIds.length <= 1) {
        // Se não tem filhos, usar exclusão normal
        console.warn("Item não tem filhos, usando exclusão normal");
        return;
      }

      console.log("🗂️ Iniciando exclusão de seção:", {
        itemId,
        hierarquia: items[itemIndex].hierarquia,
        totalItens: sectionIds.length,
      });

      // Destacar os itens e mostrar o modal
      setSectionDeleteItems(sectionIds);
      setShowSectionDeleteModal(true);
      setSectionDeleteError(null);
      setDeletingSectionProgress(null);
    },
    [items],
  );

  // Handler para cancelar exclusão de seção
  const handleCancelSectionDelete = useCallback(() => {
    setSectionDeleteItems([]);
    setShowSectionDeleteModal(false);
    setSectionDeleteError(null);
    setDeletingSectionProgress(null);
  }, []);

  // Handler para confirmar e executar a exclusão de seção
  const handleConfirmSectionDelete = useCallback(async () => {
    // Verificar se temos todas as informações necessárias
    if (!jobId || !document?.id || !apiBaseUrl || !token) {
      console.error(
        "Informações necessárias para excluir seção não disponíveis",
      );
      setSectionDeleteError("Erro: informações necessárias não disponíveis");
      return;
    }

    if (sectionDeleteItems.length === 0) {
      console.error("Nenhum item selecionado para exclusão");
      return;
    }

    // Total de itens a deletar
    const totalToDelete = sectionDeleteItems.length;

    console.log("🗑️ Iniciando exclusão de seção com", totalToDelete, "itens");

    // Iniciar processo de exclusão
    setDeletingSectionProgress({ current: 0, total: totalToDelete });
    setSectionDeleteError(null);

    // Deletar itens sequencialmente (do último para o primeiro para manter a ordem)
    // Invertemos a ordem para deletar filhos antes dos pais
    const itemsToDelete = [...sectionDeleteItems].reverse();
    let deletedCount = 0;
    let failedItems = [];

    for (const itemId of itemsToDelete) {
      try {
        const url = `${apiBaseUrl}/doc_creator/review/delete-item/${jobId}`;
        console.log(
          `🗑️ Excluindo item ${deletedCount + 1}/${totalToDelete}:`,
          itemId,
        );

        const response = await fetch(url, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
          body: JSON.stringify({
            documento_id: document.id,
            item_id: itemId,
          }),
        });

        if (!response.ok) {
          let errorMsg = `Erro ao excluir item ${itemId}`;
          try {
            const errorData = await response.json();
            errorMsg = errorData?.message || errorData?.detail || errorMsg;
          } catch {}
          console.error("❌", errorMsg);
          failedItems.push({ itemId, error: errorMsg });
        } else {
          await response.json(); // Consumir resposta
          console.log(`✅ Item ${itemId} excluído com sucesso`);
          deletedCount++;
        }

        // Atualizar progresso
        setDeletingSectionProgress({
          current: deletedCount,
          total: totalToDelete,
        });
      } catch (error) {
        console.error("❌ Erro de conexão ao excluir item:", error);
        failedItems.push({ itemId, error: "Erro de conexão" });
      }
    }

    // Verificar se houve falhas
    if (failedItems.length > 0) {
      console.error("❌ Falha ao excluir alguns itens:", failedItems);
      setSectionDeleteError(
        `Falha ao excluir ${failedItems.length} de ${totalToDelete} itens.`,
      );

      // Remover apenas os itens que foram excluídos com sucesso
      const successfullyDeletedIds = sectionDeleteItems.filter(
        (id) => !failedItems.some((f) => f.itemId === id),
      );
      setItems(
        items.filter((item) => !successfullyDeletedIds.includes(item.id)),
      );
    } else {
      console.log(
        "✅ Todos os",
        totalToDelete,
        "itens da seção foram excluídos com sucesso",
      );

      // Remover todos os itens da lista local
      setItems(items.filter((item) => !sectionDeleteItems.includes(item.id)));
    }

    // Limpar seleção após um pequeno delay para mostrar o progresso final
    setTimeout(() => {
      if (failedItems.length === 0) {
        setSectionDeleteItems([]);
        setShowSectionDeleteModal(false);
        setDeletingSectionProgress(null);
      }
    }, 500);

    setSelectedItemId(null);
  }, [jobId, document?.id, apiBaseUrl, token, sectionDeleteItems, items]);

  // Handler para quando um item é atualizado (ex: atualização de token de imagem)
  const handleItemUpdated = async (updateData) => {
    console.log("🔄 Atualizando item no DocumentEditor:", updateData);

    setItems(
      items.map((item) => {
        if (item.id === updateData.itemId) {
          // Atualizar o token da imagem e outras propriedades
          // O token vai dentro de metadata para que o DocumentItem possa usá-lo para carregar a imagem
          return {
            ...item,
            metadata: {
              ...item.metadata,
              token: updateData.token,
              coordenadas: updateData.coordenadas,
              pagina: updateData.pagina,
              documentoOrigemId: updateData.documentoOrigemId,
              dataAprovacao: updateData.dataAprovacao,
              aprovadoPor: updateData.aprovadoPor,
            },
          };
        }
        return item;
      }),
    );

    console.log(
      "✅ Item atualizado com sucesso - imagem será recarregada automaticamente",
    );

    // IMPORTANTE: Recarregar as referências do item para atualizar a lista
    // após salvar uma nova imagem
    if (updateData.itemId && showSourceViewer) {
      console.log("🔄 Recarregando referências do item após atualização...");
      const item = items.find((i) => i.id === updateData.itemId);
      if (item) {
        await handleViewSources(item);
      }
    }
  };

  const handleGlobalClick = () => {
    if (selectedItemId) {
      setSelectedItemId(null);
      setEditingItemId(null);
    }
    if (showDeleteConfirm) {
      setShowDeleteConfirm(null);
    }
  };

  // Calcular estatísticas do documento
  const totalItems = items.length;
  const reviewedItems = items.filter((item) => item.reviewed).length;
  const reviewProgress =
    totalItems > 0 ? Math.round((reviewedItems / totalItems) * 100) : 0;

  return (
    <div className="doc-editor-root">
      {/* Painel principal do editor */}
      <div
        className="doc-editor-main-panel"
        style={{ width: showSourceViewer ? `${mainPanelWidth}%` : "100%" }}
        onClick={handleGlobalClick}
      >
        <div
          className="ikaro-container-centered"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cabeçalho do documento - condicional */}
          {showHeader && (
            <div className="doc-editor-header">
              <h1 className="doc-editor-title">
                {document?.title || "Documento sem título"}
              </h1>

              {/* Barra de progresso da revisão */}
              <div className="mb-16">
                <div className="flex-row-between mb-8 text-base text-muted">
                  <span>Progresso da revisão</span>
                  <span className="font-medium text-dark">
                    {reviewProgress}%
                  </span>
                </div>
                <div className="ikaro-progress-track">
                  <div
                    className="ikaro-progress-fill ikaro-progress-fill-success"
                    style={{ width: `${reviewProgress}%` }}
                  />
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFinishDocument && onFinishDocument(document);
                }}
                className="ikaro-btn ikaro-btn-lg ikaro-btn-primary"
              >
                <Check size={18} />
                Finalizar Documento
              </button>
            </div>
          )}

          {/* Controles de visualização - Fixo ao rolar */}
          <div className="doc-editor-sticky-controls">
            {/* Seletor de documentos */}
            {documents && documents.length > 0 && (
              <div className="flex-row gap-8">
                <label htmlFor="document-selector" className="ikaro-label">
                  Documento:
                </label>
                <div className="relative" style={{ minWidth: "200px" }}>
                  <select
                    id="document-selector"
                    className="ikaro-select"
                    value={selectedDocumentId || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (onDocumentSelect) {
                        onDocumentSelect(e.target.value);
                      }
                    }}
                  >
                    {documents.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.title}
                      </option>
                    ))}
                  </select>
                  <div className="ikaro-select-arrow">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 4L6 8L10 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Checkboxes de configuração */}
            <div className="flex-row gap-24">
              <div className="flex-row gap-8">
                <input
                  type="checkbox"
                  id="show-dividers"
                  className="ikaro-checkbox"
                  checked={showDividers}
                  onChange={(e) => {
                    e.stopPropagation();
                    setShowDividers(e.target.checked);
                  }}
                />
                <label
                  htmlFor="show-dividers"
                  className="ikaro-label-clickable"
                >
                  Mostrar separadores entre itens
                </label>
              </div>

              <div className="flex-row gap-8">
                <input
                  type="checkbox"
                  id="multi-select-mode"
                  className="ikaro-checkbox"
                  checked={multiSelectMode}
                  onChange={(e) => {
                    e.stopPropagation();
                    setMultiSelectMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedReviewItems(new Set());
                    } else {
                      if (selectedItemId) {
                        setSelectedItemId(null);
                      }
                      if (editingItemId) {
                        setEditingItemId(null);
                      }
                    }
                  }}
                />
                <label
                  htmlFor="multi-select-mode"
                  className="ikaro-label-clickable"
                >
                  Selecionar vários itens para revisão
                </label>
              </div>

              {/* Toggle: modo "Editar tudo" — coloca todos os textos em edição.
                  Só faz sentido quando o documento é editável e não está em modo seleção múltipla. */}
              {editavel && !multiSelectMode && (
                <div className="flex-row gap-8">
                  <input
                    type="checkbox"
                    id="edit-all-mode"
                    className="ikaro-checkbox"
                    checked={editAllMode}
                    onChange={(e) => {
                      e.stopPropagation();
                      const nextValue = e.target.checked;
                      setEditAllMode(nextValue);
                      // Ao entrar no modo: limpa o "editingItemId" pontual, todos os textos viram editáveis.
                      // Ao sair: também limpa, e o blur de cada Quill ao desmontar dispara o autosave pendente.
                      setEditingItemId(null);
                      if (nextValue) setSelectedItemId(null);
                    }}
                  />
                  <label
                    htmlFor="edit-all-mode"
                    className="ikaro-label-clickable"
                  >
                    Editar todos os textos
                  </label>
                </div>
              )}
            </div>

            {/* Indicador de documento não editável */}
            {!editavel && (
              <div className="ikaro-badge-warning ml-auto">
                🔒 Somente leitura
              </div>
            )}
          </div>

          {/* Container único do documento (Paper) */}
          <div className="doc-editor-document-paper">
            <div className="flex-col">
              {items.map((item, itemIndex) => (
                <div key={item.id} id={`document-item-${item.id}`}>
                  {/* Barra hover ANTES do primeiro item — atalho para inserir no topo */}
                  {editavel && itemIndex === 0 && (
                    <InsertHoverBar
                      onClick={() => {
                        setShowInsertMenu(`${item.id}-above`);
                        setSelectedItemId(null);
                      }}
                    />
                  )}
                  {showInsertMenu === `${item.id}-above` && (
                    <div className="doc-editor-insert-menu-wrapper">
                      <InsertMenu
                        onInsertText={() =>
                          handleInsertItem(item.id, "above", "text")
                        }
                        onInsertImage={() =>
                          handleInsertItem(item.id, "above", "image")
                        }
                        onInsertTema={
                          cellId
                            ? () => handleOpenTemaSearch(item.id, "above")
                            : null
                        }
                        onCancel={() => setShowInsertMenu(null)}
                      />
                    </div>
                  )}

                  <DocumentItem
                    item={item}
                    itemIndex={itemIndex}
                    totalItems={items.length}
                    isSelected={selectedItemId === item.id}
                    isEditing={editingItemId === item.id || (editAllMode && item.type === 'text')}
                    fluidMode={editAllMode}
                    showDeleteConfirm={showDeleteConfirm === item.id}
                    showDividers={showDividers}
                    onSelect={handleItemClick}
                    onEdit={handleEditClick}
                    onContentChange={handleContentChange}
                    onCancelEdit={handleCancelEdit}
                    onSaveEdit={handleSaveEdit}
                    onToggleReview={handleToggleReview}
                    isSelectedForReview={selectedReviewItems.has(item.id)}
                    multiSelectMode={multiSelectMode}
                    onViewSources={handleViewSources}
                    onDuplicate={handleDuplicateItem}
                    onMoveUp={handleMoveItemUp}
                    onMoveDown={handleMoveItemDown}
                    onDelete={confirmDelete}
                    onConfirmDelete={handleDeleteItem}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                    editavel={editavel}
                    serviceName="DOCUMENT_CREATION"
                    onInsertAbove={() => {
                      setShowInsertMenu(`${item.id}-above`);
                      setSelectedItemId(null);
                    }}
                    onInsertBelow={() => {
                      setShowInsertMenu(`${item.id}-below`);
                      setSelectedItemId(null);
                    }}
                    onImageUpload={handleImageUpload}
                    uploadingImage={uploadingImage === item.id}
                    savingEdit={savingEdit}
                    saveError={saveError}
                    hasChildrenItems={hasChildren(items, itemIndex)}
                    onDeleteSection={handleDeleteSectionStart}
                    isMarkedForSectionDelete={sectionDeleteItems.includes(
                      item.id,
                    )}
                    onCreateBelow={handleCreateBelow}
                    onDeleteAndFocusPrev={handleDeleteAndFocusPrev}
                    onNavigatePrev={handleNavigatePrev}
                    onNavigateNext={handleNavigateNext}
                    registerQuill={registerQuill}
                  />

                  {showInsertMenu === `${item.id}-below` && (
                    <div className="doc-editor-insert-menu-wrapper">
                      <InsertMenu
                        onInsertText={() =>
                          handleInsertItem(item.id, "below", "text")
                        }
                        onInsertImage={() =>
                          handleInsertItem(item.id, "below", "image")
                        }
                        onInsertTema={
                          cellId
                            ? () => handleOpenTemaSearch(item.id, "below")
                            : null
                        }
                        onCancel={() => setShowInsertMenu(null)}
                      />
                    </div>
                  )}
                  {/* Barra hover DEPOIS deste item — abre InsertMenu para inserir abaixo.
                      Funciona tanto entre itens quanto após o último (atalho de inserir no fim). */}
                  {editavel && (
                    <InsertHoverBar
                      onClick={() => {
                        setShowInsertMenu(`${item.id}-below`);
                        setSelectedItemId(null);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Botão vertical para abrir painel quando está fechado */}
      {!showSourceViewer && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowSourceViewer(true);
          }}
          className="doc-editor-view-btn"
          title="Abrir visualização de documentos"
        >
          <span>VISUALIZAR DOCUMENTOS</span>
        </button>
      )}

      {/* Divisor redimensionável */}
      {showSourceViewer && (
        <>
          <div
            onMouseDown={() => {
              mainResizeRef.current = true;
            }}
            className="doc-editor-resize-handle"
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "var(--ikaro-orange-primary)")
            }
            onMouseLeave={(e) => {
              if (!mainResizeRef.current) {
                e.target.style.backgroundColor = "var(--ikaro-gray-medium)";
              }
            }}
          />

          <div
            className="flex-shrink-0"
            style={{ width: `${100 - mainPanelWidth}%` }}
          >
            <DocumentSourcesSidebar
              key={`sidebar-${selectedItemId}`}
              sources={selectedSources}
              allDocuments={allDocuments}
              originDocuments={originDocuments}
              onClose={() => setShowSourceViewer(false)}
              loadingReferences={loadingReferences}
              jobId={jobId}
              documentId={document?.id}
              itemId={selectedItemId}
              currentItemType={items.find((i) => i.id === selectedItemId)?.type}
              onItemUpdated={handleItemUpdated}
              initialDocumentOrigemId={
                items.find((i) => i.id === selectedItemId)?.metadata
                  ?.documentoOrigemId
              }
              initialPageNumber={
                items.find((i) => i.id === selectedItemId)?.metadata?.pagina
              }
              initialCoordenadas={
                items.find((i) => i.id === selectedItemId)?.metadata
                  ?.coordenadas
              }
              onOriginDocumentsUpdated={onOriginDocumentsUpdated}
              editavel={editavel}
            />
          </div>
        </>
      )}

      {/* Modal flutuante de confirmação de exclusão de seção */}
      {showSectionDeleteModal && (
        <div
          className="ikaro-floating-panel ikaro-floating-panel-error"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-row gap-12 mb-16">
            <div className="ikaro-icon-circle ikaro-icon-circle-error">
              <AlertTriangle size={24} color="var(--ikaro-status-error)" />
            </div>
            <div>
              <h3
                className="text-lg font-semibold text-dark mb-0"
                style={{ margin: 0 }}
              >
                Excluir Seção Completa
              </h3>
              <p
                className="text-base text-muted mt-4"
                style={{ margin: "4px 0 0 0" }}
              >
                {deletingSectionProgress
                  ? `Excluindo ${deletingSectionProgress.current} de ${deletingSectionProgress.total} itens...`
                  : `${sectionDeleteItems.length} itens serão excluídos`}
              </p>
            </div>
          </div>

          {deletingSectionProgress && (
            <div className="mb-16">
              <div className="ikaro-progress-track">
                <div
                  className="ikaro-progress-fill ikaro-progress-fill-error"
                  style={{
                    width: `${
                      (deletingSectionProgress.current /
                        deletingSectionProgress.total) *
                      100
                    }%`,
                  }}
                />
              </div>
              <p
                className="text-xs text-muted text-center"
                style={{ margin: "8px 0 0 0" }}
              >
                {Math.round(
                  (deletingSectionProgress.current /
                    deletingSectionProgress.total) *
                    100,
                )}
                % concluído
              </p>
            </div>
          )}

          {sectionDeleteError && (
            <div className="ikaro-alert ikaro-alert-error ikaro-alert-bordered mb-16 text-sm">
              <AlertTriangle size={16} />
              {sectionDeleteError}
            </div>
          )}

          {!deletingSectionProgress && (
            <p className="text-sm text-muted mb-16 line-height-1-5">
              Os itens destacados em{" "}
              <span
                className="font-semibold"
                style={{ color: "var(--ikaro-status-error)" }}
              >
                vermelho
              </span>{" "}
              serão excluídos permanentemente. Role a página para verificar
              todos os itens selecionados.
            </p>
          )}

          <div className="flex-row-end gap-12">
            {!deletingSectionProgress && (
              <>
                <button
                  onClick={handleCancelSectionDelete}
                  className="ikaro-btn ikaro-btn-md ikaro-btn-secondary"
                >
                  <X size={18} />
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmSectionDelete}
                  className="ikaro-btn ikaro-btn-md ikaro-btn-danger"
                >
                  <Trash2 size={18} />
                  Confirmar Exclusão
                </button>
              </>
            )}

            {deletingSectionProgress && !sectionDeleteError && (
              <div className="flex-row gap-8 text-muted text-base">
                <Loader size={18} className="spin-animation" />
                Excluindo itens...
              </div>
            )}

            {sectionDeleteError && (
              <button
                onClick={handleCancelSectionDelete}
                className="ikaro-btn ikaro-btn-md ikaro-btn-secondary"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Botão flutuante para aprovar itens selecionados */}
      {multiSelectMode && selectedReviewItems.size > 0 && (
        <div className="ikaro-floating-action">
          <span className="text-base text-dark font-medium">
            {selectedReviewItems.size}{" "}
            {selectedReviewItems.size === 1
              ? "item selecionado"
              : "itens selecionados"}
          </span>
          <button
            onClick={handleApproveSelectedItems}
            className="ikaro-btn ikaro-btn-md ikaro-btn-primary font-semibold"
            style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}
          >
            <Check size={18} />
            Aprovar Selecionados
          </button>
        </div>
      )}

      {/* Modal de Pesquisa de Temas */}
      <TemaSearchModal
        isOpen={showTemaSearchModal}
        onClose={handleCloseTemaSearch}
        onImportTema={handleImportTema}
        cellId={cellId}
        position={temaInsertPosition}
        referenceItemId={temaReferenceItemId}
      />

      {/* Disclaimer após importar tema */}
      <PopUp
        open={showTemaDisclaimer}
        onClose={() => setShowTemaDisclaimer(false)}
        title={t("temaDisclaimer.title")}
      >
        <div className="ikaro-alert ikaro-alert-warning p-16 gap-12">
          <AlertCircle
            size={24}
            className="flex-shrink-0"
            style={{ color: "var(--ikaro-status-warning)" }}
          />
          <p
            className="text-base line-height-1-5 text-dark"
            style={{ margin: 0 }}
          >
            {t("temaDisclaimer.message")}
          </p>
        </div>
        <div className="flex-row-end mt-16">
          <button
            onClick={() => setShowTemaDisclaimer(false)}
            className="ikaro-btn ikaro-btn-md ikaro-btn-primary font-semibold"
          >
            {t("temaDisclaimer.understood")}
          </button>
        </div>
      </PopUp>
    </div>
  );
};

export default DocumentEditor;
